import React, {useCallback, useEffect, useReducer, useRef} from 'react';
import styled from 'styled-components';
import {
  COLORS,
  ELEVATIONS,
  SIZES,
  Chips,
  Chip,
  ChipLabel,
  ChipIconContainer,
  Typography,
  LoadingIndicator,
} from '@fupa/fupa-uikit';
import {useDispatch, useSelector} from 'react-redux';
import SearchResult from 'app/components/search/searchResult';
import {FlexContainer} from 'app/styles/content';
import debounce from 'app/helpers/debounce';
import {cancelPendingRequest, getSearch} from 'app/services/api';
import {Helmet} from 'react-helmet-async';
import SkyscraperAdSlot from 'app/components/ads/SkyscraperAdSlot';
import {IconButton, InputAdornment, TextField} from '@mui/material';
import {Person, Search, Delete, Cancel, EmojiEvents, InsertComment, Place} from '@mui/icons-material';
import {Emblem} from 'app/components/icons';
import {changeHeaderText} from 'app/actions/headerActions';
import {useLocation} from 'react-router';
import {getSearchHistory, ALL_FILTERS, setSearchHistory} from 'app/helpers/searchHelpers';
import SearchReducer from 'app/reducers/searchReducer';
import {
  updateSearchHistory,
  updateSearchHydrated,
  updateSearchResults,
  updateSearchValue,
  updateSearchActiveFilter,
} from 'app/actions/searchActions';
import {sendEventToGA4} from 'app/helpers/gtagHelpers';
import {selectDistrict} from 'app/hooks/reduxCreateSelectorHooks';
import StickyMobileBannerAdSlot from 'app/components/ads/StickyMobileBannerAdSlot';

const Modal = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  ${({theme}) => theme.desktopLayout`
    top: ${SIZES['72']};
  `}
`;

const Wrapper = styled.div`
  display: flex;
  width: inherit;
  height: inherit;
  background-color: ${COLORS.white};
  max-width: ${props => props.theme.desktopContentWidth}px;
  margin: 0 auto;
  padding-top: env(safe-area-inset-top);
`;

const SearchTextField = styled.div`
  position: relative;
  width: 100%;
  margin-left: 0.5rem;
`;

const InputForm = styled.div`
  width: 100%;
  max-width: ${props => props.theme.desktopContentWidth}px;
  background-color: ${COLORS.white};
  position: fixed;
  overflow: hidden;
  height: ${SIZES['64']};
  display: flex;
  flex: 0 100%;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 1rem;
  box-shadow: ${ELEVATIONS.AppBar};
  z-index: 30;
  flex-wrap: nowrap;
`;

const Container = styled.div`
  width: 100%;
  margin: ${SIZES['64']} 0;
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
  padding: 1rem env(safe-area-inset-right) 1rem env(safe-area-inset-left);
  padding-bottom: 132px; // needed for ad-sticky-mobile-banner
  ${props => props.theme.desktopLayout`
    padding: 0 3rem;
  `}
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  margin: 1rem 0 ${SIZES['8']};
`;

const SearchResultCaption = styled(Typography)`
  margin-top: 1rem;
`;

const PlaceholderContainer = styled(FlexContainer)`
  flex-direction: column;
  align-items: center;
  padding-top: ${SIZES['16']};
`;

const PlaceholderText = styled(Typography)`
  padding: ${SIZES['16']};
  text-align: center;
`;
const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${SIZES[64]};
  width: ${SIZES[64]};
  border-radius: 50%;
  background-color: ${COLORS.lightestGrey};
`;

const IconActionWrapper = styled.div`
  display: flex;
  cursor: pointer;
`;

const SearchPlaceholder = ({text}) => {
  return (
    <PlaceholderContainer>
      <IconWrapper>
        <Search style={{color: COLORS.fupaSecondary, fontSize: SIZES[40]}} />
      </IconWrapper>
      <PlaceholderText variant='body1'>{text}</PlaceholderText>
    </PlaceholderContainer>
  );
};

const SearchPage = () => {
  const initialState = {
    hydrated: false,
    value: '',
    results: [],
    activeFilter: [],
    history: getSearchHistory(),
  };
  const [state, dispatch] = useReducer(SearchReducer, initialState);
  const {hydrated, value, results, activeFilter, history} = state;
  const searchTerm = useRef(value);
  const dispatchRedux = useDispatch();
  const location = useLocation();
  const {region} = useSelector(selectDistrict);
  const defaultFilters = location?.state?.filters ?? ALL_FILTERS;
  const searchEvent = useCallback(
    debounce((searchValue, filters, region) => {
      getSearch(searchValue, filters, region).then(response => {
        // For cancelled axios calls, response is undefined
        if (response) {
          dispatch(updateSearchResults(response.data));
        }
      });
    }, 250),
    []
  );

  useEffect(() => {
    if (!history.length) {
      dispatch(updateSearchHistory(getSearchHistory()));
    }
  }, []);

  useEffect(() => {
    dispatchRedux(changeHeaderText({title: 'Suche'}));
  }, []);

  useEffect(() => {
    // update component state after hydration has finished to avoid hydration errors for styled components
    dispatch(updateSearchHydrated(true));
  }, []);

  useEffect(() => {
    return () => {
      searchEvent.cancel();
      cancelPendingRequest();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (searchTerm.current) {
        sendEventToGA4('search', {search_term: searchTerm.current, content_group: 'search_index'});
      }
    };
  }, []);

  useEffect(() => {
    if (value.trim().length >= 3) {
      const searchFilters = activeFilter.length ? activeFilter : defaultFilters;
      searchEvent(value, searchFilters, region?.slug);
    }
  }, [value, activeFilter, region]);

  const updateValue = event => {
    const value = event.target.value;
    searchTerm.current = value;
    dispatch(updateSearchValue(value));
  };

  const handleClear = () => {
    dispatch(updateSearchValue(''));
    dispatch(updateSearchResults([]));
  };

  const handleClearHistory = () => {
    dispatch(updateSearchHistory([]));
    setSearchHistory(JSON.stringify([]));
  };

  const meta = [
    {
      name: 'description',
      content:
        'Suche nach Spielern und Trainern, Schiris, Teams, Ligen und Turnieren oder News-Titeln, um Inhalte zu finden.',
    },
  ];

  const SearchHistory = history.map((result, index) => <SearchResult key={result.id} result={result} index={index} />);
  const SearchResults = results.length ? (
    results.map((result, index) => <SearchResult key={result.id + index} result={result} index={index} />)
  ) : (
    <SearchPlaceholder text='Es wurden keine Suchergebnisse gefunden.' />
  );

  const filterMap = {
    player: {label: 'Spieler', icon: <Person style={{color: COLORS.darkGrey, fontSize: 20, margin: 'auto'}} />},
    teamseason: {label: 'Teams', icon: <Emblem style={{color: COLORS.darkGrey, fontSize: 16, margin: 'auto'}} />},
    competition: {
      label: 'Ligen/Turniere',
      icon: <EmojiEvents style={{color: COLORS.darkGrey, fontSize: 20, margin: 'auto'}} />,
    },
    district: {label: 'Region', icon: <Place style={{color: COLORS.darkGrey, fontSize: 16, margin: 'auto'}} />},
    referee: {label: 'Schiedsrichter', icon: <Person style={{color: COLORS.darkGrey, fontSize: 20, margin: 'auto'}} />},
    news: {label: 'News', icon: <InsertComment style={{color: COLORS.darkGrey, fontSize: 16, margin: 'auto'}} />},
  };

  let content;

  if (!hydrated) {
    content = (
      <Container>
        <LoadingIndicator message={'Letzte Suchanfragen werden geladen...'} bgColor={false} />
      </Container>
    );
  } else if (history.length && value === '' && defaultFilters === ALL_FILTERS) {
    content = (
      <Container>
        <ContentHeader>
          <Typography variant='caption1'>Letzte Suchanfragen</Typography>
          <IconActionWrapper onClick={handleClearHistory}>
            <Delete style={{color: COLORS.darkGrey, fontSize: 16}} />
          </IconActionWrapper>
        </ContentHeader>
        {SearchHistory}
      </Container>
    );
  } else {
    const filters = defaultFilters.map(currFilter => {
      const {label, icon} = filterMap[currFilter];
      return (
        <Chip
          key={currFilter}
          onClick={() => dispatch(updateSearchActiveFilter(currFilter))}
          active={activeFilter.indexOf(currFilter) > -1}>
          <ChipIconContainer>{icon}</ChipIconContainer>
          <ChipLabel>{label}</ChipLabel>
        </Chip>
      );
    });

    const resultContent =
      value !== '' ? (
        SearchResults
      ) : (
        <SearchPlaceholder text='Suche z.B. nach "Thomas Müller Bayern", "Borussia Dortmund" oder "A-Klasse München"' />
      );

    content = (
      <Container id='search'>
        <ContentHeader>
          <Typography variant='caption1'>Filter</Typography>
        </ContentHeader>
        <Chips>{filters}</Chips>
        <ContentHeader>
          <SearchResultCaption variant='caption1'>Suchergebnisse</SearchResultCaption>
        </ContentHeader>
        {resultContent}
      </Container>
    );
  }

  return (
    <Modal>
      <Wrapper>
        <Helmet title='Suche' meta={meta} />
        <InputForm>
          <SearchTextField>
            <TextField
              autoFocus={true}
              color='secondary'
              fullWidth={true}
              id='search'
              name='search'
              onChange={updateValue}
              placeholder='Suche'
              autoComplete='off'
              value={value}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton tabIndex={1} aria-label='Textfeld zurücksetzen' onClick={handleClear} size='large'>
                      <Cancel style={{color: COLORS.darkGrey, fontSize: 24}} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </SearchTextField>
        </InputForm>
        {content}
        <SkyscraperAdSlot skyscraperOnly noTabs />
        <SkyscraperAdSlot left skyscraperOnly noTabs />
      </Wrapper>
      <StickyMobileBannerAdSlot />
    </Modal>
  );
};

export default SearchPage;
